/**
 * ReadOnlyScope — hides action controls (create/edit/delete/submit/status/upload…) inside it.
 * Matches button labels, titles and aria-labels; mark an element with data-allow-readonly to keep it.
 */
import { ReactNode, useEffect, useRef } from 'react';

const ACTION_RE = /\b(add|create|edit|update|delete|remove|approve|reject|import|upload|save|submit|wipe|reset|assign|confirm|mark|accept|decline|complete|quote)\b/i;

export function ReadOnlyScope({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const apply = () => {
      root.querySelectorAll<HTMLElement>('button, [role="switch"], [role="combobox"], input[type="file"]').forEach(el => {
        if (el.closest('[data-allow-readonly]') || el.hasAttribute('data-ro-hidden')) return;
        const role = el.getAttribute('role');
        const text = `${el.getAttribute('aria-label') ?? ''} ${el.getAttribute('title') ?? ''} ${el.textContent ?? ''}`;
        const isAction = role === 'switch' || (el as HTMLInputElement).type === 'file' || ACTION_RE.test(text);
        if (isAction) { el.setAttribute('data-ro-hidden', ''); el.style.display = 'none'; }
      });
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(root, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className="contents">{children}</div>;
}
